import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Text } from '@chakra-ui/react';

import { useAppDispatch } from '../../../../redux/hooks/app-hooks';
import { sendMetrics } from '../../../../redux/thunks/metrics-thunk';
import { MixpanelEvent } from '../../../../services/mixpanel/types';

import { MessageToolCallProvider } from './ToolCallResults/MessageToolCallProvider';
import { ToolResultRenderer } from './ToolCallResults/ToolResultRenderer';

import styles from './EnhancedMessage.module.scss';

export type ToolResultsProps = {
    conversationId: string;
    messageId: string;
    count: number;
};

const ToolResultsComponent: React.FC<ToolResultsProps> = ({ conversationId, messageId, count }) => {
    const { t } = useTranslation('translations');
    const dispatch = useAppDispatch();
    // performance optimization, render only if a user actually expands the accordion item with the results
    // does not unmount the component when the accordion is closed
    const [wasShown, setWasShown] = useState(false);

    // if there are no results to display,
    // there is no reason to display an empty accordion item
    if (!count) {
        return null;
    }

    const onClick = () => {
        if (!wasShown) {
            setWasShown(true);

            dispatch(
                sendMetrics({
                    event: MixpanelEvent.CHAT_MESSAGE_TOOL_RESULTS_EXPAND,
                    meta: {
                        message_id: messageId,
                    },
                })
            );
        }
    };

    return (
        <AccordionItem borderColor="transparent">
            <Box className={styles.accordionHeader}>
                <AccordionButton onClick={onClick}>
                    <AccordionIcon />
                </AccordionButton>
                <Text className={styles.title}>
                    {t('chat-tab.search-results')}
                    <span className={styles.resultsCount}>{count}</span>
                </Text>
            </Box>

            <AccordionPanel>
                <MessageToolCallProvider conversationId={conversationId} messageId={messageId}>
                    {/* use wasShown to render the results only when the accordion is expanded by a user, and keep the component mounted */}
                    {wasShown && <ToolResultRenderer />}
                </MessageToolCallProvider>
            </AccordionPanel>
        </AccordionItem>
    );
};

export const ToolResults = React.memo(ToolResultsComponent);
